/**
 * @fileoverview useTransactionEdit - Transaction edit mode state management.
 *
 * Manages all state and operations for editing existing transactions. Mirrors
 * the structure of useTransactionForm but for edit mode, with additional logic
 * for ceased category checking and transaction rebuilding.
 *
 * @description This hook provides:
 * - Edit mode state (parallel to add form state)
 * - Transaction loading into edit form
 * - Ceased category validation (prevents editing transactions with ceased categories)
 * - Edit form validation
 * - Transaction object rebuilding for API updates
 * - Edit mode cancellation
 *
 * Key features:
 * - Prevents editing transactions with ceased categories (shows alert)
 * - Handles expense transactions with user categories correctly
 * - Converts amounts to absolute values for editing
 * - Validates before allowing save
 *
 * @module hooks/useTransactionEdit
 */
import { useState, useEffect, useRef } from 'react';
import { validateTransaction, hasValidationErrors } from '../utils/validationUtils';
import { buildTransactionFromEdit } from './helpers/transactionBuilder';

/**
 * Hook for managing transaction edit state.
 *
 * @param {Array<string>} userCategories - List of user category names
 * @param {Array<Object>} categoryObjects - Full category objects with endDate info
 * @param {boolean} singleBankMode - Whether budget is in single bank mode (optional)
 * @param {Array<Object>} users - List of user objects with userKey and userName
 * @returns {Object} Edit state and operations
 * @returns {number|null} return.editingId - ID of transaction being edited
 * @returns {string} return.editTransactionType - Transaction type being edited
 * @returns {string} return.editAmount - Edit form amount
 * @returns {string} return.editDescription - Edit form description
 * @returns {string} return.editDate - Edit form date
 * @returns {Array} return.editTransactionCategories - Edit form categories
 * @returns {Object} return.editErrors - Validation errors for edit form
 * @returns {Function} return.startEdit - Load transaction into edit form
 * @returns {Function} return.buildUpdatedTransaction - Build updated transaction object
 * @returns {Function} return.validateEdit - Validate edit form
 * @returns {Function} return.cancelEdit - Cancel editing and clear form
 *
 * @example
 * const editState = useTransactionEdit(userCategories, categories, false, users);
 * editState.startEdit(transaction); // Loads transaction into edit form
 * if (editState.validateEdit()) {
 *   const updated = editState.buildUpdatedTransaction();
 *   // Save to API
 * }
 */
export const useTransactionEdit = (userCategories, categoryObjects, singleBankMode = false, users = []) => {
    // Edit transaction state
    const [editingId, setEditingId] = useState(null);
    const [editTransactionType, setEditTransactionType] = useState('');
    const [editAmount, setEditAmount] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editDate, setEditDate] = useState('');
    const [editTransactionCategories, setEditTransactionCategories] = useState([
        { category: '', amount: '' }
    ]);

    // Adjustment-specific state
    const [editAdjustmentFromCategory, setEditAdjustmentFromCategory] = useState('');
    const [editAdjustmentToCategory, setEditAdjustmentToCategory] = useState('');

    // Edit-specific user state
    // users is an array of objects: { userKey, userName }
    const getDefaultEditUserKey = () => users[0]?.userKey || null;
    const [editCurrentUserKey, setEditCurrentUserKey] = useState(getDefaultEditUserKey);
    const [editPaidDirectly, setEditPaidDirectly] = useState(false);
    const [editUseTemplateForIncome, setEditUseTemplateForIncome] = useState(false);

    // Track initial categories for grandfathering (categories when edit started)
    const [initialEditCategories, setInitialEditCategories] = useState([]);

    // Version for optimistic locking (conflict detection)
    const [editVersion, setEditVersion] = useState(null);

    // Error state
    const [editErrors, setEditErrors] = useState({});

    // Track if we're loading a transaction to avoid auto-filling during load
    const isLoadingTransaction = useRef(false);

    // Auto-set editCurrentUserKey when users array becomes available
    // This ensures a default is set even when the dropdown is hidden
    useEffect(() => {
        if (editCurrentUserKey === null && users && users.length > 0) {
            setEditCurrentUserKey(getDefaultEditUserKey());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users]);

    // Auto-fill first category amount when total amount changes (same logic as add form)
    useEffect(() => {
        // Skip if we're loading a transaction
        if (isLoadingTransaction.current) {
            return;
        }

        // Only auto-fill for Expense and Income types (not Transfer, Adjustment, or template income)
        if ((editTransactionType === 'Expense' || editTransactionType === 'Income') && !editUseTemplateForIncome) {
            if (editAmount && editTransactionCategories.length >= 1) {
                const sumOfOtherCategories = editTransactionCategories
                    .slice(1) // Skip first category
                    .reduce((sum, cat) => sum + (parseFloat(cat.amount) || 0), 0);

                const firstCategoryAmount = parseFloat(editAmount) - sumOfOtherCategories;
                const newAmount = firstCategoryAmount >= 0 ? firstCategoryAmount.toFixed(2) : '0.00';

                // Only update if the amount actually changed
                if (editTransactionCategories[0].amount !== newAmount) {
                    setEditTransactionCategories(prev => [
                        { ...prev[0], amount: newAmount },
                        ...prev.slice(1)
                    ]);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editAmount, editTransactionType, editUseTemplateForIncome]);

    // Start editing a transaction
    const startEdit = (transaction) => {
        // Set flag to prevent auto-fill during load
        isLoadingTransaction.current = true;

        // Check if transaction contains any ceased categories
        const ceasedCategories = [];

        if (transaction.type === 'Adjustment') {
            // Check adjustment categories
            const fromCat = categoryObjects.find(cat => cat.categoryName === transaction.adjustmentFromCategory);
            const toCat = categoryObjects.find(cat => cat.categoryName === transaction.adjustmentToCategory);

            if (fromCat && fromCat.endDate) {
                ceasedCategories.push(transaction.adjustmentFromCategory);
            }
            if (toCat && toCat.endDate) {
                ceasedCategories.push(transaction.adjustmentToCategory);
            }
        } else {
            // Check regular categories
            transaction.categories.forEach(cat => {
                const categoryObj = categoryObjects.find(c => c.categoryName === cat.category);
                if (categoryObj && categoryObj.endDate) {
                    ceasedCategories.push(cat.category);
                }
            });
        }

        // If there are ceased categories, show alert and prevent edit
        if (ceasedCategories.length > 0) {
            alert(
                `Cannot edit this transaction because it contains ceased categories:\n\n` +
                ceasedCategories.join(', ') +
                `\n\nPlease reactivate these categories first.`
            );
            isLoadingTransaction.current = false;
            return;
        }

        setEditingId(transaction.id);
        setEditVersion(transaction.version || null); // Store version for optimistic locking
        setEditAmount(Math.abs(transaction.amount).toString());
        setEditDescription(transaction.description);
        setEditDate(transaction.date);
        setEditTransactionType(transaction.type);
        // Use paidByUserKey from the transaction (set by backend)
        setEditCurrentUserKey(transaction.paidByUserKey || null);
        setEditPaidDirectly(transaction.paidDirectly || false);
        setEditUseTemplateForIncome(transaction.usedTemplate || false);

        if (transaction.type === 'Adjustment') {
            // For adjustments, extract from/to from the categories array if not in top-level fields
            const fromCategory = transaction.adjustmentFromCategory ||
                                transaction.categories.find(cat => cat.amount < 0)?.category || '';
            const toCategory = transaction.adjustmentToCategory ||
                              transaction.categories.find(cat => cat.amount > 0)?.category || '';

            setEditAdjustmentFromCategory(fromCategory);
            setEditAdjustmentToCategory(toCategory);
            setEditTransactionCategories([{ category: '', amount: '' }]);
        } else {
            setEditAdjustmentFromCategory('');
            setEditAdjustmentToCategory('');

            let categoriesToEdit;
            if (transaction.type === 'Expense') {
                categoriesToEdit = transaction.categories.filter(cat =>
                    !userCategories.includes(cat.category)
                );
            } else {
                categoriesToEdit = transaction.categories;
            }

            const mappedCategories = categoriesToEdit.map(cat => ({
                category: cat.category,
                amount: Math.abs(cat.amount).toString()
            }));
            setEditTransactionCategories(mappedCategories);

            // Save initial categories for grandfathering
            setInitialEditCategories(mappedCategories.map(cat => cat.category));
        }

        // Clear the loading flag after a brief delay to allow state to settle
        setTimeout(() => {
            isLoadingTransaction.current = false;
        }, 0);
    };

    // Build updated transaction object for saving
    const buildUpdatedTransaction = () => {
        return buildTransactionFromEdit({
            editingId,
            editVersion,  // Include version for optimistic locking
            editTransactionType,
            editAmount,
            editDescription,
            editAdjustmentFromCategory,
            editAdjustmentToCategory,
            editDate,
            editCurrentUserKey,
            editPaidDirectly,
            editUseTemplateForIncome,
            editTransactionCategories
        }, userCategories, users);
    };

    // Validate edit form
    const validateEdit = () => {
        const validationErrors = validateTransaction({
            amount: editAmount,
            description: editDescription,
            transactionCategories: editTransactionType === 'Adjustment' ? [] : editTransactionCategories,
            date: editDate,
            transactionType: editTransactionType,
            currentUserKey: editCurrentUserKey,
            adjustmentFromCategory: editAdjustmentFromCategory,
            adjustmentToCategory: editAdjustmentToCategory
        }, singleBankMode);

        if (hasValidationErrors(validationErrors)) {
            setEditErrors(validationErrors);
            return false;
        }

        setEditErrors({});
        return true;
    };

    // Cancel editing
    const cancelEdit = () => {
        setEditingId(null);
        setEditVersion(null);
        setEditAmount('');
        setEditDescription('');
        setEditTransactionCategories([{ category: '', amount: '' }]);
        setEditAdjustmentFromCategory('');
        setEditAdjustmentToCategory('');
        setEditDate('');
        setEditTransactionType('');
        setEditErrors({});
        setEditPaidDirectly(false);
        setEditCurrentUserKey(users[0]?.userKey || null);
        setEditUseTemplateForIncome(false);
        setInitialEditCategories([]);
    };

    return {
        // Edit state
        editingId,
        editVersion,
        setEditVersion,
        editTransactionType,
        setEditTransactionType,
        editAmount,
        setEditAmount,
        editDescription,
        setEditDescription,
        editDate,
        setEditDate,
        editTransactionCategories,
        setEditTransactionCategories,
        editAdjustmentFromCategory,
        setEditAdjustmentFromCategory,
        editAdjustmentToCategory,
        setEditAdjustmentToCategory,
        editCurrentUserKey,
        setEditCurrentUserKey,
        editPaidDirectly,
        setEditPaidDirectly,
        editUseTemplateForIncome,
        setEditUseTemplateForIncome,

        // Initial categories for grandfathering
        initialEditCategories,

        // Error state
        editErrors,
        setEditErrors,

        // Actions
        startEdit,
        buildUpdatedTransaction,
        validateEdit,
        cancelEdit
    };
};
